import { LogUtil } from 'components/common/LogUtil';
import Cookies from 'helpers/cookies';
import { useDiscountLoadingState } from 'hooks/discount-loading/DiscountLoadingContext';
import { MAKE_PAYMENT_KEY } from 'hooks/form-add-transaction/useAddTransaction';
import useQuery from 'hooks/useQuery';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { compareServiceAlertsStore } from '../helpers/compares';
import useServiceAlertsByCountryCodeListener from '../listeners/useServiceAlertsByCountryCodeListener';
import {
  ServiceAlertsByCountryCodeDefault,
  ServiceAlertsByPaymentsDefault,
  ServiceAlertsStoreModal,
} from '../model/ServiceAlertsLocalStore';
import {
  SERVICE_ALERTS_TYPE,
  ServiceAlertsModel,
} from '../model/ServiceAlertsModel';
import useCheckServiceAlerts from '../useCheckServiceAlerts';
import useServiceAlertsConfig from '../useServiceAlertsConfig';
import useServiceAlertsStore from '../useServiceAlertsStore';
import {
  applyServiceAlertDiscountGate,
  computeSlotDiscountGate,
  shouldDeferRangeDisableEvaluation,
} from './discountGate';
import useServiceAlertsObserver from './useServiceAlertsObserver';

const TAG = 'service-alerts-countryCode-observer';

interface Props {
  dbPath?: string;
  variant: string;
}
const useServiceAlertsByCountryCodeObserver = ({ dbPath, variant }: Props) => {
  const query = useQuery();
  const refNumber = query.get('refNumber');
  const isMakePayment = Cookies.get(MAKE_PAYMENT_KEY) || refNumber;
  const isPreventObserver = isMakePayment;

  const {
    serviceAlertsByCountryCode: serviceAlertsStore,
    setServiceAlertsByCountryCode: setServiceAlerts,
    setServiceAlertsByPayments,
  } = useServiceAlertsStore();

  const {
    countryCode,
    payoutMethod,
    payoutMethodByBank,
    payoutMethodItem,
    currencyCode1,
    currencyCode2,
    receivingAmount1,
    receivingAmount2,
  } = useServiceAlertsConfig();

  const [serviceAlertsByCountryCodeData, setServiceAlertsByCountryCodeData] =
    useState<ServiceAlertsModel | null>(null);

  // Previous currency/amount inputs, used to re-show a still-applicable alert
  // when the user changes the currency or receiving amount on Step 2.
  const prevInputsRef = useRef<{
    currencyCode1?: string;
    receivingAmount1?: number;
    currencyCode2?: string;
    receivingAmount2?: number;
  }>({});

  // While discounts are validating/loading (Step 2 ver2, via the
  // DiscountLoadingProvider), defer showing a service-alert popup; re-show it
  // once discounts settle. isProvided is false on pages without the provider
  // (base AddTransactionPage), where the prior behaviour is preserved.
  const {
    isProvided: isDiscountGatingActive,
    isDiscountSettled,
    amountInputEventSeq1,
    amountInputEventSeq2,
  } = useDiscountLoadingState();
  const wasDiscountBusyRef = useRef(false);
  // Last amount-input event seen per slot. A change since the previous run is a
  // real user amount change -- including one the sampled amount cannot show,
  // because the value round-tripped inside a single debounce window.
  const seenAmountEventSeq1Ref = useRef(amountInputEventSeq1);
  const seenAmountEventSeq2Ref = useRef(amountInputEventSeq2);
  const pendingShow1Ref = useRef(false);
  const pendingShow2Ref = useRef(false);
  // Currency-level re-show pending, tracked separately from the range/input
  // pending above: an amount-only change must not re-show an already-dismissed
  // currency-level alert (a currency alert depends on the currency, not the
  // amount).
  const pendingShowCurrency1Ref = useRef(false);
  const pendingShowCurrency2Ref = useRef(false);

  const { fetchServiceAlertsListener, closeServiceAlertsListener } =
    useServiceAlertsObserver({
      variant,
      dbPath: dbPath || '',
    });

  const {
    checkByCountryCode,
    checkByPayoutMethod,
    checkByPayoutMethodItem,
    checkByPayoutMethodItemChild,
    checkByCurrencyCode,
    checkByRangeOfCurrencyCode,
  } = useCheckServiceAlerts();
  const { getPreventShowServiceAlertsModalByCountryCode } =
    checkByCountryCode || {};
  const { getPreventShowServiceAlertsModalByPayoutMethod } =
    checkByPayoutMethod || {};
  const {
    getServiceAlertsByPayoutMethodItem,
    getPayoutMethodItemId,
    getPreventShowServiceAlertsModalByPayoutMethodItem,
  } = checkByPayoutMethodItem || {};
  const {
    getPreventShowServiceAlertsModalByPayoutMethodItemChild,
    getServiceAlertsByPayoutMethodItemChild,
    getPayoutMethodItemChildId,
  } = checkByPayoutMethodItemChild || {};

  const {
    getServiceAlertsByCurrencyCode,
    getPreventShowServiceAlertsModalByCurrencyCode,
  } = checkByCurrencyCode();
  const {
    getServiceAlertsByRangeOfCurrencyCode,
    getPreventShowServiceAlertsModalByRangeOfCurrencyCode,
  } = checkByRangeOfCurrencyCode();

  useEffect(() => {
    const isAllowConnectToRealtimeDb =
      !isPreventObserver && countryCode && dbPath;

    if (isAllowConnectToRealtimeDb) {
      fetchServiceAlertsListener({
        path: dbPath,
        setServiceAlerts: setServiceAlertsByCountryCodeData,
      });
    }

    return () => {
      if (isAllowConnectToRealtimeDb) {
        closeServiceAlertsListener({
          path: dbPath,
        });

        setServiceAlertsByCountryCodeData(null);

        setServiceAlerts(ServiceAlertsByCountryCodeDefault);

        setServiceAlertsByPayments(ServiceAlertsByPaymentsDefault);
      }
    };
  }, [isPreventObserver, countryCode, dbPath]);

  const {
    countryCodeListener,
    payoutMethodListener,
    payoutMethodItemListener,
    payoutMethodItemChildListener,
    currencyCode1Listener,
    currencyCode2Listener,
    rangeOfCurrencyCode1Listener,
    rangeOfCurrencyCode2Listener,
  } = useServiceAlertsByCountryCodeListener();
  useEffect(() => {
    if (!isPreventObserver && !isEmpty(serviceAlertsByCountryCodeData)) {
      const prevInputs = prevInputsRef.current;
      // A genuine user change to a non-empty amount/currency. Guard on a non-empty
      // amount: a reset (e.g. closing a range-level DISABLE alert) clears the
      // amount to 0, and re-showing the parent DELAY then is unwanted; a DELAY
      // with no amount entered is meaningless.
      const isCurrency1InputChanged =
        receivingAmount1 > 0 &&
        (currencyCode1 !== prevInputs.currencyCode1 ||
          receivingAmount1 !== prevInputs.receivingAmount1);
      const isCurrency2InputChanged =
        receivingAmount2 > 0 &&
        (currencyCode2 !== prevInputs.currencyCode2 ||
          receivingAmount2 !== prevInputs.receivingAmount2);
      // Currency-code-only change (independent of amount). Drives the
      // currency-level re-show so an amount entry does not re-pop a dismissed
      // currency-level alert; the initial null -> code selection counts as a
      // change and so flushes a genuinely deferred first show.
      const isCurrency1Changed = currencyCode1 !== prevInputs.currencyCode1;
      const isCurrency2Changed = currencyCode2 !== prevInputs.currencyCode2;

      // Discount gating decision (pure logic in ./discountGate). While discounts
      // are validating/loading, defer the popup and re-show it once they settle;
      // the per-listener DELAY-only scoping is applied at each listener call.
      const isDiscountBusy = isDiscountGatingActive && !isDiscountSettled;
      const isDiscountJustSettled =
        wasDiscountBusyRef.current && !isDiscountBusy;
      // A real amount input event since the previous run, reported by the
      // fee-fetch completion callback. This is what the amount sampler cannot
      // see, and what discount state must not be used to guess at: a discount
      // cycle also starts on remount, so inferring an input from it re-showed
      // dismissed alerts on step navigation. See computeSlotDiscountGate.
      const isAmountInputEvent1 =
        amountInputEventSeq1 !== seenAmountEventSeq1Ref.current;
      const isAmountInputEvent2 =
        amountInputEventSeq2 !== seenAmountEventSeq2Ref.current;

      const slot1Gate = computeSlotDiscountGate({
        isGatingActive: isDiscountGatingActive,
        isDiscountBusy,
        isDiscountJustSettled,
        isAmountInputEvent: isAmountInputEvent1,
        isInputChanged: isCurrency1InputChanged,
        isCurrencyChanged: isCurrency1Changed,
        hasAmount: receivingAmount1 > 0,
        pendingShow: pendingShow1Ref.current,
        pendingShowCurrency: pendingShowCurrency1Ref.current,
      });
      const slot2Gate = computeSlotDiscountGate({
        isGatingActive: isDiscountGatingActive,
        isDiscountBusy,
        isDiscountJustSettled,
        isAmountInputEvent: isAmountInputEvent2,
        isInputChanged: isCurrency2InputChanged,
        isCurrencyChanged: isCurrency2Changed,
        hasAmount: receivingAmount2 > 0,
        pendingShow: pendingShow2Ref.current,
        pendingShowCurrency: pendingShowCurrency2Ref.current,
      });
      pendingShow1Ref.current = slot1Gate.nextPendingShow;
      pendingShow2Ref.current = slot2Gate.nextPendingShow;
      pendingShowCurrency1Ref.current = slot1Gate.nextPendingShowCurrency;
      pendingShowCurrency2Ref.current = slot2Gate.nextPendingShowCurrency;
      const serviceAlertsByCountryCode = serviceAlertsByCountryCodeData || {};
      const serviceAlertsByPayoutMethod =
        serviceAlertsByCountryCodeData?.[payoutMethod || ''];
      const payoutItemId =
        payoutMethodItem &&
        serviceAlertsByPayoutMethod?.type !== SERVICE_ALERTS_TYPE.DISABLE &&
        getPayoutMethodItemId(payoutMethod || '');
      const serviceAlertsByPayoutMethodItem =
        payoutItemId &&
        getServiceAlertsByPayoutMethodItem({
          payoutMethod: payoutMethod || '',
          serviceAlertsByPayoutMethod,
        });
      const payoutItemChildId =
        payoutMethodItem &&
        serviceAlertsByPayoutMethod?.type !== SERVICE_ALERTS_TYPE.DISABLE &&
        getPayoutMethodItemChildId(payoutMethod || '');
      const serviceAlertsByPayoutMethodItemChild =
        payoutItemChildId &&
        getServiceAlertsByPayoutMethodItemChild({
          payoutMethod: payoutMethod || '',
          serviceAlertsByPayoutMethodItem,
        });
      const serviceAlertsByCurrencyCode1 =
        currencyCode1 &&
        getServiceAlertsByCurrencyCode({
          currencyCode: currencyCode1,
          serviceAlertsByCountryCode,
          serviceAlertsByPayoutMethod,
          serviceAlertsByPayoutMethodItem,
          serviceAlertsByPayoutMethodItemChild:
            serviceAlertsByPayoutMethodItem?.[payoutItemChildId],
        });
      const serviceAlertsByCurrencyCode2 =
        currencyCode2 &&
        getServiceAlertsByCurrencyCode({
          currencyCode: currencyCode2,
          serviceAlertsByCountryCode,
          serviceAlertsByPayoutMethod,
          serviceAlertsByPayoutMethodItem,
          serviceAlertsByPayoutMethodItemChild:
            serviceAlertsByPayoutMethodItem?.[payoutItemChildId],
        });
      const serviceAlertsByRangeOfCurrencyCode1: any =
        (receivingAmount1 &&
          getServiceAlertsByRangeOfCurrencyCode({
            currencyCode: currencyCode1,
            receivingAmount: receivingAmount1,
            serviceAlertsByCountryCode,
            serviceAlertsByPayoutMethod,
            serviceAlertsByPayoutMethodItem,
            serviceAlertsByPayoutMethodItemChild:
              serviceAlertsByPayoutMethodItem?.[payoutItemChildId],
          })) ||
        null;
      const currencyCode1ContainRange =
        receivingAmount1 && !isEmpty(serviceAlertsByRangeOfCurrencyCode1);
      const serviceAlertsByRangeOfCurrencyCode2: any =
        (receivingAmount2 &&
          getServiceAlertsByRangeOfCurrencyCode({
            currencyCode: currencyCode2,
            receivingAmount: receivingAmount2,
            serviceAlertsByCountryCode,
            serviceAlertsByPayoutMethod,
            serviceAlertsByPayoutMethodItem,
            serviceAlertsByPayoutMethodItemChild:
              serviceAlertsByPayoutMethodItem?.[payoutItemChildId],
          })) ||
        null;
      const currencyCode2ContainRange =
        receivingAmount2 && !isEmpty(serviceAlertsByRangeOfCurrencyCode2);

      // A range-level DISABLE popped as soon as an amount was entered, i.e.
      // against a receiving amount the rate fetch had not settled yet. It
      // cannot be deferred like a DELAY (modal suppressed, alert still
      // recorded): its store write drives the Remittance form reset, which
      // would then fire with no modal to explain it. Skip the listener
      // entirely while unsettled so the record, the modal and the reset land
      // together on the settle run. See shouldDeferRangeDisableEvaluation.
      const isDeferRange1Disable = shouldDeferRangeDisableEvaluation({
        isGatingActive: isDiscountGatingActive,
        isDisableAlert:
          serviceAlertsByRangeOfCurrencyCode1?.type ===
          SERVICE_ALERTS_TYPE.DISABLE,
        deferByDiscountRaw: slot1Gate.deferByDiscountRaw,
      });
      const isDeferRange2Disable = shouldDeferRangeDisableEvaluation({
        isGatingActive: isDiscountGatingActive,
        isDisableAlert:
          serviceAlertsByRangeOfCurrencyCode2?.type ===
          SERVICE_ALERTS_TYPE.DISABLE,
        deferByDiscountRaw: slot2Gate.deferByDiscountRaw,
      });

      let newServiceAlertsStore: ServiceAlertsStoreModal | any = {
        ...serviceAlertsStore,
        serviceAlertsByCountryCodeData,
        ...(!countryCode && { serviceAlertsByCountryCodeData: null }),
        ...(!countryCode && { serviceAlertsByCountryCode: null }),
        ...(!payoutMethod && { serviceAlertsByPayoutMethod: null }),
        ...(!payoutItemId && { serviceAlertsByPayoutMethodItem: null }),
        ...(!payoutMethodByBank && { showValidateBankAccountModal: null }),
        ...(!payoutItemChildId && {
          serviceAlertsByPayoutMethodItemChild: null,
        }),
        ...(!currencyCode1 && { serviceAlertsByCurrencyCode1: null }),
        ...(!currencyCode2 && { serviceAlertsByCurrencyCode2: null }),
        ...(!currencyCode1ContainRange && {
          serviceAlertsByRangeOfCurrencyCode1: null,
        }),
        ...(!currencyCode2ContainRange && {
          serviceAlertsByRangeOfCurrencyCode2: null,
        }),
      };

      if (countryCode) {
        newServiceAlertsStore = countryCodeListener({
          data: serviceAlertsByCountryCode,
          serviceAlertsStore: newServiceAlertsStore,
          isPreventShowServiceAlertsModal:
            getPreventShowServiceAlertsModalByCountryCode(),
        });
      }
      if (payoutMethod) {
        newServiceAlertsStore = payoutMethodListener({
          data: serviceAlertsByPayoutMethod,
          serviceAlertsStore: newServiceAlertsStore,
          isPreventShowServiceAlertsModal:
            getPreventShowServiceAlertsModalByPayoutMethod({
              serviceAlertsStore: newServiceAlertsStore,
              payoutItemId,
              payoutItemChildId,
            }),
        });
      }
      if (payoutItemId) {
        newServiceAlertsStore = payoutMethodItemListener({
          data: serviceAlertsByPayoutMethodItem,
          serviceAlertsStore: newServiceAlertsStore,
          isPreventShowServiceAlertsModal:
            getPreventShowServiceAlertsModalByPayoutMethodItem({
              serviceAlertsStore: newServiceAlertsStore,
              payoutItemId,
              payoutItemChildId,
            }),
        });
      }
      if (payoutItemChildId) {
        newServiceAlertsStore = payoutMethodItemChildListener({
          data: serviceAlertsByPayoutMethodItemChild,
          serviceAlertsStore: newServiceAlertsStore,
          isPreventShowServiceAlertsModal:
            getPreventShowServiceAlertsModalByPayoutMethodItemChild({
              serviceAlertsStore: newServiceAlertsStore,
              payoutMethod: payoutMethod || '',
              payoutItemId,
              payoutItemChildId,
            }),
        });
      }
      if (currencyCode1) {
        const gate = applyServiceAlertDiscountGate({
          isGatingActive: isDiscountGatingActive,
          isDelayAlert:
            serviceAlertsByCurrencyCode1?.type === SERVICE_ALERTS_TYPE.DELAY,
          deferByDiscountRaw: slot1Gate.deferByDiscountRaw,
          // Currency-level listener re-shows on a currency change only, so an
          // amount entry does not re-pop a dismissed currency-level alert.
          baseForceReshow: slot1Gate.baseForceReshowCurrency,
        });
        newServiceAlertsStore = currencyCode1Listener({
          data: serviceAlertsByCurrencyCode1,
          serviceAlertsStore: newServiceAlertsStore,
          isForceReshow: gate.forceReshow,
          isPreventShowServiceAlertsModal:
            gate.deferByDiscount ||
            getPreventShowServiceAlertsModalByCurrencyCode({
              serviceAlertsStore: newServiceAlertsStore,
              payoutMethod,
              payoutItemId,
              payoutItemChildId,
              serviceAlertByCurrencyCode: serviceAlertsByCurrencyCode1,
              serviceAlertsByRangeOfCurrencyCode: null,
            }),
        });
      }
      if (currencyCode2) {
        const gate = applyServiceAlertDiscountGate({
          isGatingActive: isDiscountGatingActive,
          isDelayAlert:
            serviceAlertsByCurrencyCode2?.type === SERVICE_ALERTS_TYPE.DELAY,
          deferByDiscountRaw: slot2Gate.deferByDiscountRaw,
          // Currency-level listener re-shows on a currency change only, so an
          // amount entry does not re-pop a dismissed currency-level alert.
          baseForceReshow: slot2Gate.baseForceReshowCurrency,
        });
        newServiceAlertsStore = currencyCode2Listener({
          data: serviceAlertsByCurrencyCode2,
          serviceAlertsStore: newServiceAlertsStore,
          isForceReshow: gate.forceReshow,
          isPreventShowServiceAlertsModal:
            gate.deferByDiscount ||
            getPreventShowServiceAlertsModalByCurrencyCode({
              serviceAlertsStore: newServiceAlertsStore,
              payoutMethod,
              payoutItemId,
              payoutItemChildId,
              serviceAlertByCurrencyCode: serviceAlertsByCurrencyCode2,
              serviceAlertsByRangeOfCurrencyCode: null,
            }),
        });
      }
      if (currencyCode1ContainRange && !isDeferRange1Disable) {
        const gate = applyServiceAlertDiscountGate({
          isGatingActive: isDiscountGatingActive,
          isDelayAlert:
            serviceAlertsByRangeOfCurrencyCode1?.type ===
            SERVICE_ALERTS_TYPE.DELAY,
          deferByDiscountRaw: slot1Gate.deferByDiscountRaw,
          baseForceReshow: slot1Gate.baseForceReshow,
        });
        newServiceAlertsStore = rangeOfCurrencyCode1Listener({
          data: serviceAlertsByRangeOfCurrencyCode1,
          serviceAlertsStore: newServiceAlertsStore,
          isForceReshow: gate.forceReshow,
          // A range show withheld on this run owes itself a re-show once
          // discounts settle -- but only when the user actually changed this
          // slot's inputs. A suppression alone is not evidence of that: step
          // navigation nulls this slot and re-records it, which reaches onGet
          // with the popup suppressed and would otherwise owe a re-show for an
          // alert the user had already dismissed without touching anything.
          onSuppressedShow: () => {
            if (isCurrency1InputChanged || isAmountInputEvent1) {
              pendingShow1Ref.current = true;
            }
          },
          isPreventOnGet:
            serviceAlertsByRangeOfCurrencyCode1?.type !==
            SERVICE_ALERTS_TYPE.DISABLE,
          isPreventShowServiceAlertsModal:
            gate.deferByDiscount ||
            getPreventShowServiceAlertsModalByRangeOfCurrencyCode({
              serviceAlertsStore: newServiceAlertsStore,
              payoutMethod,
              payoutItemId,
              payoutItemChildId,
              serviceAlertByCurrencyCode: serviceAlertsByCurrencyCode1,
            }),
        });
      }
      if (currencyCode2ContainRange && !isDeferRange2Disable) {
        const gate = applyServiceAlertDiscountGate({
          isGatingActive: isDiscountGatingActive,
          isDelayAlert:
            serviceAlertsByRangeOfCurrencyCode2?.type ===
            SERVICE_ALERTS_TYPE.DELAY,
          deferByDiscountRaw: slot2Gate.deferByDiscountRaw,
          baseForceReshow: slot2Gate.baseForceReshow,
        });
        newServiceAlertsStore = rangeOfCurrencyCode2Listener({
          data: serviceAlertsByRangeOfCurrencyCode2,
          serviceAlertsStore: newServiceAlertsStore,
          isForceReshow: gate.forceReshow,
          // See the slot-1 note above.
          onSuppressedShow: () => {
            if (isCurrency2InputChanged || isAmountInputEvent2) {
              pendingShow2Ref.current = true;
            }
          },
          isPreventOnGet:
            serviceAlertsByRangeOfCurrencyCode2?.type !==
            SERVICE_ALERTS_TYPE.DISABLE,
          isPreventShowServiceAlertsModal:
            gate.deferByDiscount ||
            getPreventShowServiceAlertsModalByRangeOfCurrencyCode({
              serviceAlertsStore: newServiceAlertsStore,
              payoutMethod,
              payoutItemId,
              payoutItemChildId,
              serviceAlertByCurrencyCode: serviceAlertsByCurrencyCode2,
            }),
        });
      }

      const isServiceAlertsStoreChange = !compareServiceAlertsStore(
        serviceAlertsStore,
        newServiceAlertsStore
      );
      if (isServiceAlertsStoreChange) {
        LogUtil.log(
          TAG,
          `serviceAlertsStore xxxx data xxxx ${variant}`,
          JSON.stringify(newServiceAlertsStore)
        );
        setServiceAlerts(newServiceAlertsStore);
      }

      prevInputsRef.current = {
        currencyCode1,
        receivingAmount1,
        currencyCode2,
        receivingAmount2,
      };
      wasDiscountBusyRef.current = isDiscountBusy;
      seenAmountEventSeq1Ref.current = amountInputEventSeq1;
      seenAmountEventSeq2Ref.current = amountInputEventSeq2;
    }
  }, [
    isPreventObserver,
    isDiscountGatingActive,
    isDiscountSettled,
    amountInputEventSeq1,
    amountInputEventSeq2,
    serviceAlertsByCountryCodeData,
    serviceAlertsStore,
    countryCode,
    payoutMethod,
    payoutMethodItem,
    currencyCode1,
    currencyCode2,
    receivingAmount1,
    receivingAmount2,
  ]);
};

export default useServiceAlertsByCountryCodeObserver;
